import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import columnToTypeIcon from 'data-frames/util/columnToTypeIcon';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import {Size} from 'ui-library/inputs/InputConstants';
import TextInput from 'ui-library/inputs/TextInput';
import {InputChangeEvent} from 'util/Types';

import {DataFrameRenameColumnOptions} from './DataFrameHeaderMenuOptions';
import {DataFrameSharedPopover} from './DataFrameSharedPopover';

export interface DataFrameRenameCreatorProps {
  canSelectColumn?: boolean;
  columns: Array<DataFrameColumnDef>;
  renameColumnOptions: DataFrameRenameColumnOptions;
  onCancel?: () => void;
  onSave?: () => void;
  initialSelectedColumnName: string;
}

interface InternalDataFrameRenameCreatorProps {
  column: DataFrameColumnDef;
  onUpdateRename: (name: string) => void;
}

function InternalDataFrameRenameCreator({
  column,
  onUpdateRename,
}: InternalDataFrameRenameCreatorProps): JSX.Element {
  return (
    <div className={css(styles.renameSection)}>
      <Chip
        className={css(styles.chip)}
        size={ChipSize.SMALL}
        icon={columnToTypeIcon(column)}
        text={column.displayName}
        isReadonly={true}
      />
      <TextInput
        onChange={(e: InputChangeEvent) => {
          onUpdateRename(e.target.value);
        }}
        placeholder="New name"
        size={Size.SMALL}
        autoFocus={true}
      />
    </div>
  );
}

/**
 * Create a rename transformation.
 */
export function DataFrameRenameCreator({
  canSelectColumn,
  columns,
  renameColumnOptions,
  onCancel,
  onSave,
  initialSelectedColumnName,
}: DataFrameRenameCreatorProps): JSX.Element {
  const [selectedColumnSystemName, setSelectedColumnSystemName] = useState<string>(initialSelectedColumnName);

  const defaultNewNameValue = '';
  const [cachedName, setCachedName] = useState<string | undefined>(defaultNewNameValue);

  function onColumnSystemNameChange(columnSystemName: string): void {
    setSelectedColumnSystemName(columnSystemName);
    setCachedName(defaultNewNameValue);
  }
  const column = columns.find((column) => column.systemName === selectedColumnSystemName)!;
  const isDisabled =
    (cachedName != null && cachedName?.length === 0) ||
    cachedName === columns.find((column) => selectedColumnSystemName === column.systemName)?.displayName;

  function handleUpdateRename(name?: string): void {
    setCachedName(name);
  }

  function handleCancel(): void {
    setCachedName(defaultNewNameValue);
    onCancel?.();
  }

  function handleSave(): void {
    renameColumnOptions.renameColumn(selectedColumnSystemName, cachedName);
    onSave?.();
  }

  return (
    <DataFrameSharedPopover
      canSelectColumn={canSelectColumn}
      columns={columns}
      onCancel={handleCancel}
      onSave={handleSave}
      isDisabled={isDisabled}
      selectedColumnSystemName={selectedColumnSystemName}
      onColumnSystemNameChange={onColumnSystemNameChange}
      internalCreator={
        <InternalDataFrameRenameCreator
          column={column}
          onUpdateRename={(name: string) => {
            handleUpdateRename(name);
          }}
        />
      }
    />
  );
}

export const styles = StyleSheet.create({
  renameSection: {
    alignItems: 'center',
    display: 'flex',
    marginBottom: 10,
  },
  chip: {
    minWidth: 125,
  },
});
